// Telemetria de uso: telas (com tempo visível), cliques em [data-track],
// buscas e filtros. Envia em lote com fetch keepalive (sobrevive ao fechar a
// aba e leva o header Authorization). Qualquer falha é ignorada em silêncio:
// telemetria nunca pode quebrar tela nem mostrar erro.
import { getUser } from "@/components/user";
import { versoes } from "@/router/views/versao/versao";
import { mascarar, classificarTermo } from "./mascara";

const INTERVALO_MS = 15000;
const LIMITE_LOTE = 50;
const DEBOUNCE_BUSCA_MS = 1000;
// Chips de filtro cujo valor é texto livre ou nome de pessoa (aluno/usuário):
// só o TIPO do termo sai do navegador, nunca o valor.
const CHAVES_TEXTO_LIVRE = new Set(["nome", "atendimento", "usuario"]);

let fila = [];
let telaAtual = null; // { nome, acumuladoMs, visivelDesde }
let timerBusca = null;
let routerRef = null;
let instalado = false;
let sessaoMemoria = null;

function uuid() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function sessaoId() {
  try {
    let id = window.sessionStorage.getItem("telemetria_sessao");
    if (!id) {
      id = uuid();
      window.sessionStorage.setItem("telemetria_sessao", id);
    }
    return id;
  } catch (e) {
    if (!sessaoMemoria) sessaoMemoria = uuid();
    return sessaoMemoria;
  }
}

function nomeTela(route) {
  if (!route) return "desconhecida";
  return route.name ? String(route.name) : route.path || "desconhecida";
}

function telaCorrente() {
  if (telaAtual) return telaAtual.nome;
  return nomeTela(routerRef && routerRef.currentRoute ? routerRef.currentRoute.value : null);
}

function registrar(tipo, { tela, alvo = null, valor = null, duracao_ms = null }) {
  try {
    if (!window.localStorage.getItem("token")) return;
    const user = getUser();
    fila.push({
      sessao_id: sessaoId(),
      tipo,
      tela: tela || "desconhecida",
      alvo,
      valor,
      duracao_ms,
      versao: versoes && versoes[0] ? versoes[0].versao : null,
      polo_id: user && user.polo ? user.polo.id : null,
      ocorrido_em: new Date().toISOString(),
    });
    if (fila.length >= LIMITE_LOTE) enviar();
  } catch (e) {
    // silencioso
  }
}

function enviar() {
  try {
    if (!fila.length) return;
    const lote = fila.splice(0, LIMITE_LOTE);
    const token = window.localStorage.getItem("token");
    if (!token) return;
    fetch(process.env.VUE_APP_API + "/telemetria/eventos", {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ eventos: lote }),
    }).catch(() => {});
  } catch (e) {
    // silencioso
  }
}

function pausarTela() {
  if (telaAtual && telaAtual.visivelDesde) {
    telaAtual.acumuladoMs += Date.now() - telaAtual.visivelDesde;
    telaAtual.visivelDesde = null;
  }
}

function retomarTela() {
  if (telaAtual && !telaAtual.visivelDesde) telaAtual.visivelDesde = Date.now();
}

function iniciarTela(route) {
  telaAtual = {
    nome: nomeTela(route),
    acumuladoMs: 0,
    visivelDesde: document.visibilityState === "visible" ? Date.now() : null,
  };
}

function fecharTela() {
  if (!telaAtual) return;
  pausarTela();
  registrar("tela", { tela: telaAtual.nome, duracao_ms: Math.round(telaAtual.acumuladoMs) });
  telaAtual = null;
}

function onClick(e) {
  try {
    const el = e.target && e.target.closest ? e.target.closest("[data-track]") : null;
    if (!el) return;
    registrar("clique", { tela: telaCorrente(), alvo: el.getAttribute("data-track") });
  } catch (err) {
    // silencioso
  }
}

export function registrarBusca(alvo, valor) {
  try {
    clearTimeout(timerBusca);
    const tipo = classificarTermo(valor);
    if (!tipo) return;
    const tela = telaCorrente();
    timerBusca = setTimeout(() => registrar("busca", { tela, alvo, valor: tipo }), DEBOUNCE_BUSCA_MS);
  } catch (e) {
    // silencioso
  }
}

export function registrarBuscaImediata(alvo, valor) {
  try {
    const tipo = classificarTermo(valor);
    if (!tipo) return;
    registrar("busca", { tela: telaCorrente(), alvo, valor: tipo });
  } catch (e) {
    // silencioso
  }
}

export function registrarFiltros(chips) {
  try {
    const tela = telaCorrente();
    (chips || []).forEach((c) => {
      const valor = CHAVES_TEXTO_LIVRE.has(String(c.key))
        ? classificarTermo(String(c.label ?? "").replace(/^[^:]*:\s*/, ""))
        : mascarar(c.label);
      registrar("filtro", { tela, alvo: String(c.key), valor });
    });
  } catch (e) {
    // silencioso
  }
}

export function instalarTelemetria(router) {
  if (instalado) return;
  instalado = true;
  try {
    routerRef = router;
    router.afterEach((to) => {
      // Mudança só de query/params na mesma tela não conta como nova visita.
      if (telaAtual && telaAtual.nome === nomeTela(to)) return;
      fecharTela();
      iniciarTela(to);
    });
    document.addEventListener("click", onClick, true);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        pausarTela();
        enviar();
      } else {
        retomarTela();
      }
    });
    window.addEventListener("pagehide", () => {
      fecharTela();
      enviar();
    });
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) iniciarTela(router.currentRoute.value);
    });
    setInterval(enviar, INTERVALO_MS);
  } catch (e) {
    // silencioso
  }
}
